﻿<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import AppDialog from './AppDialog.vue'
import AppIcon from './AppIcon.vue'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: []; captured: [file: File] }>()

const video = ref<HTMLVideoElement | null>(null)
const error = ref('')
const starting = ref(false)
const facingMode = ref<'environment' | 'user'>('environment')
const fallbackInput = ref<HTMLInputElement | null>(null)
let stream: MediaStream | null = null

function stop(): void {
  for (const track of stream?.getTracks() ?? []) track.stop()
  stream = null
  if (video.value) video.value.srcObject = null
}

async function start(): Promise<void> {
  stop()
  error.value = ''
  if (!navigator.mediaDevices?.getUserMedia) {
    error.value = 'Live camera is not available in this browser.'
    return
  }

  starting.value = true
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: facingMode.value },
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    })
    await nextTick()
    if (video.value) {
      video.value.srcObject = stream
      await video.value.play()
    }
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : 'Unable to access the camera.'
  } finally {
    starting.value = false
  }
}

async function switchCamera(): Promise<void> {
  facingMode.value = facingMode.value === 'environment' ? 'user' : 'environment'
  await start()
}

function timestampName(): string {
  const value = new Date().toISOString().replace(/[-:]/g, '').replace('T', '-').slice(0, 15)
  return `foto-${value}.jpg`
}

async function capture(): Promise<void> {
  if (!video.value || !video.value.videoWidth) return
  const canvas = document.createElement('canvas')
  canvas.width = video.value.videoWidth
  canvas.height = video.value.videoHeight
  const context = canvas.getContext('2d')
  if (!context) return
  context.drawImage(video.value, 0, 0, canvas.width, canvas.height)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.94))
  if (!blob) return
  stop()
  emit('captured', new File([blob], timestampName(), { type: 'image/jpeg', lastModified: Date.now() }))
}

function fallbackSelected(files: FileList | null): void {
  const file = files?.[0]
  if (file) emit('captured', file)
}

watch(() => props.open, (open) => {
  if (open) void start()
  else stop()
})

onBeforeUnmount(stop)
</script>

<template>
  <AppDialog :open="open" title="Take a photo" wide full-screen-mobile @close="emit('close')">
    <template #eyebrow>Camera</template>
    <div class="camera-stage">
      <video ref="video" class="camera-video" autoplay muted playsinline />
      <div v-if="starting" class="camera-overlay"><span class="spinner" /></div>
      <div v-if="error" class="camera-error">
        <AppIcon name="warning" :size="28" />
        <strong>Camera not available</strong>
        <p>{{ error }}</p>
        <button type="button" class="button button--secondary" @click="fallbackInput?.click()">Open device camera</button>
        <input ref="fallbackInput" class="visually-hidden" type="file" accept="image/*" capture="environment" @change="fallbackSelected(($event.target as HTMLInputElement).files)" />
      </div>
    </div>
    <template #footer>
      <button type="button" class="button button--ghost" @click="switchCamera">
        <AppIcon name="switch" :size="18" /> Switch camera
      </button>
      <button type="button" class="button button--primary camera-shutter" :disabled="starting || Boolean(error)" @click="capture">
        <AppIcon name="camera" :size="21" /> Capture
      </button>
    </template>
  </AppDialog>
</template>
